// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { EducBox, ResultCard, InputRow, fmt } from './shared';
import { useDeepLinkParams, hasAllParams, ShareCalcButton, scrollToResult } from './deepLink';

// Valori aggiornati 2026 (Circolare INPS n. 4/2026)
const SOGLIA_1 = 1456.72;
const MASSIMALE_NASPI = 1584.70;
const RIDUZIONE_MENSILE = 0.03; // 3% dal 5° mese

// Formattazione con 2 decimali e separatore migliaia (per i parametri ufficiali INPS)
const fmt2 = (n) => new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' }).format(Number(n));

const NASpI = () => {
  const url = useDeepLinkParams({ ral: 'num', settimane: 'num' });
  // Map RAL annuo (URL) → stipendio medio mensile (input interno) usando 12 mensilità
  const stipendioFromRal = url.ral ? String(Math.round(Number(url.ral) / 12)) : null;
  const [stipendio, setStipendio] = useState(stipendioFromRal ?? '1800');
  const [settimaneContributi, setSettimaneContributi] = useState(url.settimane ?? '208'); // 4 anni

  useEffect(() => {
    if (hasAllParams(url, ['ral', 'settimane'])) scrollToResult('naspi-result');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const S = parseFloat(stipendio) || 0;
  const W = parseInt(settimaneContributi) || 0;

  // Calcolo importo NASpI
  const naspiLordo = useMemo(() => {
    if (S <= SOGLIA_1) return S * 0.75;
    return (SOGLIA_1 * 0.75) + ((S - SOGLIA_1) * 0.25);
  }, [S]);

  const naspiNetto = Math.min(naspiLordo, MASSIMALE_NASPI);
  const durMesi = Math.min(Math.round(W / 2), 24);

  // Simulazione mese per mese con riduzione
  const mensili = useMemo(() => {
    const rows = [];
    for (let m = 1; m <= durMesi; m++) {
      const riduzioniApplicate = m >= 5 ? m - 4 : 0;
      const fattore = Math.pow(1 - RIDUZIONE_MENSILE, riduzioniApplicate);
      const importo = naspiNetto * fattore;
      rows.push({ mese: m, importo: Math.max(0, importo) });
    }
    return rows;
  }, [naspiNetto, durMesi]);

  const totalePercepito = mensili.reduce((acc, r) => acc + r.importo, 0);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="naspi-calc">
      <EducBox text="La NASpI (Nuova Assicurazione Sociale per l'Impiego) è l'indennità INPS per la disoccupazione involontaria. Spetta a lavoratori dipendenti licenziati o con contratto scaduto (NON alle dimissioni volontarie, salvo giusta causa). Requisiti 2026: almeno 13 settimane di contributi negli ultimi 4 anni." />

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <InputRow
          label="Retribuzione media mensile lorda (€)"
          value={stipendio}
          onChange={setStipendio}
          prefix="€"
          tooltip="Media degli imponibili INPS negli ultimi 4 anni"
        />
        <InputRow
          label="Settimane contributi (ultimi 4 anni)"
          value={settimaneContributi}
          onChange={setSettimaneContributi}
          suffix="settimane"
          tooltip="Trovi questo dato nel tuo estratto conto contributivo INPS (myINPS)"
        />
      </div>

      {/* Calcolo step-by-step */}
      <div className="mb-6 p-4 rounded-xl text-sm" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
        <p className="text-xs text-[#888780] mb-3 font-medium">Come viene calcolato il tuo importo</p>
        <div className="space-y-1.5 text-[#888780]" data-testid="naspi-calc-breakdown">
          {S <= SOGLIA_1
            ? <p>Retribuzione ≤ €{fmt2(SOGLIA_1)}: <span className="text-[#E8E8E4]">€{fmt(S)} × 75% = <strong className="text-[#C9A84C]">€{fmt(naspiNetto)}</strong></span></p>
            : <>
                <p>Quota base: <span className="text-[#E8E8E4]">€{fmt2(SOGLIA_1)} × 75% = €{fmt(SOGLIA_1 * 0.75)}</span></p>
                <p>Quota eccedente: <span className="text-[#E8E8E4]">(€{fmt(S)} - €{fmt2(SOGLIA_1)}) × 25% = €{fmt((S - SOGLIA_1) * 0.25)}</span></p>
                <p>Totale lordo: <span className="text-[#E8E8E4]">€{fmt(naspiLordo)}</span>
                  {naspiLordo > MASSIMALE_NASPI && <span className="text-[#C9A84C] ml-1">→ limitato al massimale €{fmt2(MASSIMALE_NASPI)}</span>}
                </p>
              </>
          }
          <p>Durata: <span className="text-[#E8E8E4]">{W} settimane ÷ 2 = <strong className="text-[#C9A84C]">{durMesi} mesi</strong></span> (max 24)</p>
          <p className="pt-2 mt-1 text-xs" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} data-testid="naspi-massimale-info">
            <span className="text-[#888780]">Parametri 2026 (Circolare INPS n. 4/2026):</span>{' '}
            <span className="text-[#E8E8E4]">Soglia di calcolo <strong className="text-[#C9A84C]">€{fmt2(SOGLIA_1)}</strong> · Massimale mensile <strong className="text-[#C9A84C]">€{fmt2(MASSIMALE_NASPI)}</strong></span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="naspi-result">
        <ResultCard label="NASpI mensile (1° mese)" value={`€${fmt(naspiNetto)}`} gold />
        <ResultCard label="Durata indennità" value={`${durMesi} mesi`} />
        <ResultCard label="NASpI all'ultimo mese" value={`€${fmt(mensili[mensili.length - 1]?.importo || 0)}`} />
        <ResultCard label="Totale percepito" value={`€${fmt(totalePercepito)}`} green />
      </div>

      {/* Tabella riduzione progressiva */}
      {durMesi > 4 && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="p-3 text-xs font-medium text-[#888780]" style={{ background: 'rgba(255,255,255,0.03)' }}>
            Riduzione progressiva dal 5° mese (-3%/mese)
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="py-2 px-3 text-left text-[#888780] font-medium">Mese</th>
                  <th className="py-2 px-3 text-right text-[#888780] font-medium">Importo NASpI</th>
                  <th className="py-2 px-3 text-right text-[#888780] font-medium">Riduzione</th>
                </tr>
              </thead>
              <tbody>
                {mensili.map(row => (
                  <tr key={row.mese} className="border-b border-white/[0.04]">
                    <td className="py-1.5 px-3 text-[#888780]">{row.mese}°</td>
                    <td className="py-1.5 px-3 text-right font-medium" style={{ color: row.mese >= 5 ? '#C9A84C' : '#3D7A52' }}>€{fmt(row.importo)}</td>
                    <td className="py-1.5 px-3 text-right text-[#888780]">{row.mese >= 5 ? `-${(RIDUZIONE_MENSILE * (row.mese - 4) * 100).toFixed(0)}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ShareCalcButton slug="naspi" testid="naspi-share-btn"
        getValues={() => ({ ral: Math.round((parseFloat(stipendio) || 0) * 12), settimane: settimaneContributi })} />

      <div className="mt-4 p-4 rounded-xl text-sm text-[#888780]" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="font-medium text-[#E8E8E4] mb-2">Procedura domanda (myINPS)</p>
        <p>Presentare domanda entro <strong className="text-[#C9A84C]">68 giorni</strong> dalla cessazione del rapporto di lavoro.</p>
        <p className="mt-1">Accedi su <strong>inps.it → myINPS → NASpI</strong> con SPID o CIE.</p>
      </div>
</motion.div>
  );
};

export default NASpI;
